import { Box, Stack, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { RefreshCw, Trash2 } from "lucide-react";
import { type FC, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useDeleteIdentificationDocumentMutation } from "web_myplann/Api/Client/useDeleteIdentificationDocumentMutation";
import {
	useDocumentsFeatureOptional,
	useRemindersRightOptional,
} from "web_myplann/Api/Rights/RightsProvider";
import { ReminderEntityTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { AppRouting } from "web_myplann/Infrastructure/Utils/UrlUtils";
import { REMINDERS_SECTION_ANCHOR_ID } from "web_myplann/NotificationCenter/Utils/reminderScrollParam";
import { useScrollToReminders } from "web_myplann/Portfolio/Hooks/useScrollToReminders";
import { PaperworkActionRow } from "web_myplann/Profile/Components/Paperwork/PaperworkActionRow";
import { PaperworkDetailRow } from "web_myplann/Profile/Components/Paperwork/PaperworkDetailRow";
import { PaperworkValidityBanner } from "web_myplann/Profile/Components/Paperwork/PaperworkValidityBanner";
import { ProfileSection } from "web_myplann/Profile/Components/ProfileSection";
import {
	getDocumentValidity,
	getPaperworkDocumentDisplayName,
} from "web_myplann/Profile/Helpers/paperworkDocument";
import { usePaperworkDocument } from "web_myplann/Profile/Hooks/usePaperworkDocument";
import { RemindersSection } from "web_myplann/Reminders/Components/RemindersSection";
import { ConfirmDrawer } from "web_myplann/Shared/Components/ConfirmDrawer";
import { FlatRowGroup } from "web_myplann/Shared/Components/FlatRow/FlatRowGroup";
import { LoadErrorWithRetry } from "web_myplann/Shared/Components/LoadErrorWithRetry";
import { MyPlannLoading } from "web_myplann/Shared/Components/MyPlannLoading";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";

const R = Resources.Paperwork;

export const PaperworkDetailPage: FC = () => {
	const { palette } = useTheme();
	const { t } = useResource();
	const navigate = useNavigate();
	const { id } = useParams();
	const {
		document,
		issuer,
		isPending,
		isError,
		isSuccess,
		isRefetching,
		refetch,
	} = usePaperworkDocument(id);
	const [isConfirmOpen, setIsConfirmOpen] = useState(false);
	const { mutate: deleteDocument } = useDeleteIdentificationDocumentMutation();

	const confirmDelete = (documentId: number) => {
		setIsConfirmOpen(false);
		deleteDocument(documentId);
		navigate(AppRouting.MyPlann.Profile.Paperwork.path(), { replace: true });
	};

	const typeName = document ? t(R.Types[document.documentType]) : "";
	const title = document
		? getPaperworkDocumentDisplayName(document, typeName)
		: t(R.title);
	const { status, formatted } = getDocumentValidity(document?.validTo);
	const isRemindersEnabled = useRemindersRightOptional();
	const isDocumentsEnabled = useDocumentsFeatureOptional();
	const documentId = document?.id;
	const documentValidTo = document?.validTo;
	const reminderEntity = useMemo(() => {
		if (
			!isRemindersEnabled ||
			!isDocumentsEnabled ||
			documentId === undefined ||
			!documentValidTo
		) {
			return null;
		}
		const validTo = dayjs(documentValidTo);
		if (!validTo.isValid()) {
			return null;
		}
		return {
			type: ReminderEntityTypeEnum.IdentificationDocument,
			id: documentId,
			validTo: validTo.toDate(),
		};
	}, [isRemindersEnabled, isDocumentsEnabled, documentId, documentValidTo]);
	useScrollToReminders(reminderEntity !== null);

	return (
		<MyPlannLayout
			disableContentPadding
			bgColor={palette.grey[50]}
			topBar={
				<NavigationHeader
					title={title}
					bgColor={palette.background.appBar}
					defaultRoute={AppRouting.MyPlann.Profile.Paperwork.path()}
					alwaysUseDefaultRoute
				/>
			}
		>
			{isPending ? (
				<MyPlannLoading />
			) : document ? (
				<Stack pt={0.8} pb={4}>
					<PaperworkValidityBanner status={status} formatted={formatted} />

					<Stack mt={1.6}>
						<FlatRowGroup>
							<PaperworkDetailRow label={t(R.Detail.type)} value={typeName} />
							{document.number && (
								<PaperworkDetailRow
									label={t(R.Detail.number)}
									value={document.number}
								/>
							)}
							{issuer && (
								<PaperworkDetailRow label={t(R.Detail.issuer)} value={issuer} />
							)}
							<PaperworkDetailRow
								label={t(R.Detail.validTo)}
								value={formatted ?? t(R.Validity.noExpiration)}
								isLast
							/>
						</FlatRowGroup>
					</Stack>

					{reminderEntity && (
						<Box id={REMINDERS_SECTION_ANCHOR_ID} mt={1.6}>
							<RemindersSection entity={reminderEntity} />
						</Box>
					)}

					<ProfileSection title={t(R.Detail.Actions.title)}>
						<PaperworkActionRow
							icon={RefreshCw}
							iconColor={palette.blue[600]}
							label={t(R.Detail.Actions.update)}
							sublabel={t(R.Detail.Actions.updateHint)}
							onClick={() =>
								navigate(
									AppRouting.MyPlann.Profile.PaperworkEdit.path(document.id),
								)
							}
						/>
						<Stack ml={1.6} borderTop={1} borderColor="surface.border" />
						<PaperworkActionRow
							icon={Trash2}
							iconColor={palette.red[500]}
							label={t(R.Detail.Actions.delete)}
							destructive
							onClick={() => setIsConfirmOpen(true)}
						/>
					</ProfileSection>

					<ConfirmDrawer
						open={isConfirmOpen}
						title={t(R.Detail.DeleteConfirm.title)}
						description={t(R.Detail.DeleteConfirm.description)}
						confirmLabel={t(R.Detail.DeleteConfirm.confirm)}
						cancelLabel={t(R.Detail.DeleteConfirm.cancel)}
						destructive
						onConfirm={() => confirmDelete(document.id)}
						onClose={() => setIsConfirmOpen(false)}
					/>
				</Stack>
			) : isError ? (
				// Only reached when there is no cached document to fall back on —
				// a failed background refetch must not hide a document the user
				// already has (offline / persisted cache), so `document` wins above.
				<LoadErrorWithRetry
					title={t(R.Detail.Error.title)}
					description={t(R.Detail.Error.description)}
					retryLabel={t(R.Detail.Error.retry)}
					isRetrying={isRefetching}
					onRetry={() => refetch()}
				/>
			) : isSuccess ? (
				<Navigate to={AppRouting.MyPlann.Profile.Paperwork.path()} replace />
			) : (
				<MyPlannLoading />
			)}
		</MyPlannLayout>
	);
};
